import React, { useState } from 'react';
import { HiOutlineDeviceMobile, HiOutlineBell, HiCheck } from 'react-icons/hi';
import { useAppSetupStatus, promptInstall, enableNotifications } from '../../utils/appSetup';

/**
 * Persistent "finish setting up" card in the sidebar footer, Odoo-style: it has
 * no close button and stays until this device is installed as an app AND has
 * notifications on, then disappears by itself. A dismissible popup is what we
 * had before — users tapped "Not now" once and then missed every push.
 *
 * Each row offers the one thing that actually works on this browser: a real
 * button where the browser allows one, otherwise the steps to do it by hand
 * (iOS has no install API; a denied permission cannot be re-asked by the page).
 */
const AppSetupCard = () => {
    const status = useAppSetupStatus();
    const [busy, setBusy] = useState(false);

    if (status.complete) return null;

    const handleInstall = async () => {
        setBusy(true);
        try { await promptInstall(); } finally { setBusy(false); }
    };

    const handleNotify = async () => {
        setBusy(true);
        try { await enableNotifications(); } finally { setBusy(false); }
    };

    let installAction;
    if (status.installed) {
        installAction = <span className="app-setup-done"><HiCheck size={14} /> Installed</span>;
    } else if (status.canPromptInstall) {
        installAction = (
            <button type="button" className="app-setup-btn" onClick={handleInstall} disabled={busy}>
                Install
            </button>
        );
    } else if (status.ios) {
        installAction = <span className="app-setup-hint">Tap Share, then “Add to Home Screen”</span>;
    } else {
        installAction = <span className="app-setup-hint">Browser menu ⋮ → “Install app”</span>;
    }

    let notifyAction;
    if (status.notifications === 'granted') {
        notifyAction = <span className="app-setup-done"><HiCheck size={14} /> On</span>;
    } else if (status.notificationsNeedInstall) {
        notifyAction = <span className="app-setup-hint">Install the app first</span>;
    } else if (status.notifications === 'denied') {
        notifyAction = <span className="app-setup-hint">Blocked: allow it from the 🔒 icon in the address bar</span>;
    } else if (status.notifications === 'default') {
        notifyAction = (
            <button type="button" className="app-setup-btn" onClick={handleNotify} disabled={busy}>
                Turn on
            </button>
        );
    }

    return (
        <div className="app-setup-card" role="region" aria-label="Finish setting up PrimeOffice">
            <div className="app-setup-title">Finish setting up</div>

            <div className="app-setup-row">
                <HiOutlineDeviceMobile size={18} />
                <div className="app-setup-body">
                    <span className="app-setup-label">Install on this browser</span>
                    {installAction}
                </div>
            </div>

            {notifyAction && (
                <div className="app-setup-row">
                    <HiOutlineBell size={18} />
                    <div className="app-setup-body">
                        <span className="app-setup-label">Notifications on this browser</span>
                        {notifyAction}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppSetupCard;
